import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom';
import { logoIcon } from '../ui/media';
import { LanguagesIcon, FileQuestionIcon, FileText, MessageSquareWarning, UserLock, UserCog, CogIcon, BrainCogIcon, MessageSquareHeart, UsersIcon, Notebook, Microchip, Webhook, AlignJustify, FileQuestion, CreditCard, HeartIcon, FileIcon, FilesIcon, HeartPulseIcon, ListTodo, Eye, Users, Users2 } from 'lucide-react'
import { useUser } from "../../hooks/useAuth"

const Sidebar = ({ collapsed }) => {
  const user = useUser()
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [profileMeta, setProfileMeta] = useState(true);
  const [actionLogs, setActionLogs] = useState(true);
  const [usersMenu, setUsersMenu] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isSuccess: isUserSuccess, data: userResponse } = user
  const { REACT_APP_IMG_URL } = process.env
  const location = useLocation();
  const isCollapsed = collapsed || isMobile;

  useEffect(() => {
    if (isUserSuccess) {
      setUserData(userResponse?.data?.data)
    }
  }, [userResponse, isUserSuccess])

  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setSettingsOpen(true);
    } else {
      setSettingsOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (collapsed || isMobile) {
      setSettingsOpen(false);
    }
  }, [collapsed, isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // initialize on load
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <aside
      className={`bg-white border-r border-gray-200 transition-all duration-300 
      ${isCollapsed ? 'w-16' : 'w-64'} fixed h-full z-30 overflow-y-auto h-[91vh]`}>
      <div className="flex items-center justify-center h-14 shadow-sm border-b border-gray-200">
        <div className="flex items-center px-4">
          <img
            className="h-8 w-8"
            src={logoIcon}
            alt="Newboo Inc"
          />
          {!isCollapsed && (
            <span className="ml-2 text-lg font-semibold">Newboo Inc</span>
          )}
        </div>
      </div>
      <nav className="mt-5 px-2 space-y-1 h-[78vh] overflow-scroll">
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
            ? 'bg-gray-100 text-gray-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <svg
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'} h-6 w-6 text-gray-500`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          {!isCollapsed && <span>Dashboard</span>}
        </NavLink>
        <div className={isCollapsed ? 'px-2' : ''}>
          <div className={isCollapsed ? 'text-center' : ''}>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`w-full text-left group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname.startsWith('/settings')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`
              }>
              <CogIcon
                className={`${isCollapsed ? 'mx-auto' : 'mr-3'} h-6 w-6 text-gray-500`}
              />
              {!isCollapsed && <span>Settings</span>}
            </button>
          </div>
          {/* Submenu for Settings */}
          {settingsOpen && (
            <div className={`mt-1 space-y-1 transition-all duration-200 ease-in-out ${isCollapsed ? 'pl-0' : 'pl-6'}`}>
              <NavLink
                to="/settings/languages"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><LanguagesIcon className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <LanguagesIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Languages</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/settings/faq-categories"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <FileQuestionIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <FileQuestionIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>FAQ's</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/settings/report-reason-categories"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <MessageSquareWarning className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <MessageSquareWarning className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Report Reasons</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/settings/terms"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <FileText className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <FileText className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Terms & Conditions</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/settings/privacy"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <FileText className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <FileText className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Privacy Policy</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/settings/account"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <CogIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <CogIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Account Settings</span>
                  </span>
                )}
              </NavLink>
              {/* <NavLink
                to="/settings/application"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs">
                    <UserCog className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                  </span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <UserCog className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>App Settings</span>
                  </span>
                )}
              </NavLink> */}
            </div>
          )}
        </div>
        {/* Users Management */}
        <div className={isCollapsed ? 'px-2' : ''}>
          <div className={isCollapsed ? 'text-center' : ''}>
            <button
              onClick={() => setUsersMenu(!usersMenu)}
              className={`w-full text-left group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname.startsWith('/users')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`
              }>
              <UsersIcon
                className={`${isCollapsed ? 'mx-auto' : 'mr-3'} h-6 w-6 text-gray-500`}
              />
              {!isCollapsed && <span>Users</span>}
            </button>
          </div>
          {/* Submenu for Users */}
          {usersMenu && (
            <div className={`mt-1 space-y-1 transition-all duration-200 ease-in-out ${isCollapsed ? 'pl-0' : 'pl-6'}`}>
              <NavLink
                to="/users/all-users"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><UsersIcon className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <UsersIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>All Users</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/users/subscribed-users"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><UsersIcon className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <UsersIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Subscribed Users</span>
                  </span>
                )}
              </NavLink>
            </div>
          )}
        </div>
        {/* Content Management */}
        {/* <NavLink
          to="/subscription-plans"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <CreditCard
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>Subscription Plans</span>}
        </NavLink> */}
        {/* Reports */}
        <NavLink
          to="/onboarding"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <Microchip
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>Onboarding</span>}
        </NavLink>
        {/* Roles */}
        <NavLink
          to="/roles"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <Notebook
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>Roles</span>}
        </NavLink>
        {/* System Users */}
        <NavLink
          to="/system-users"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <UserLock
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>System Users</span>}
        </NavLink>
        {/* Interest */}
        <NavLink
          to="/interest"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <MessageSquareHeart
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>Interest</span>}
        </NavLink>
        <NavLink
          to="/user-lists"
          className={({ isActive }) =>
            `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            } ${isCollapsed ? 'justify-center' : ''}`
          }>
          <Users2
            className={`${isCollapsed ? 'mx-auto' : 'mr-3'
              } h-6 w-6 text-gray-500`}
          />
          {!isCollapsed && <span>User Lists</span>}
        </NavLink>
        <div className={isCollapsed ? 'px-2' : ''}>
          <div className={isCollapsed ? 'text-center' : ''}>
            <button
              onClick={() => setProfileMeta(!profileMeta)}
              className={`w-full text-left group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname.startsWith('/profile-meta')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`
              }>
              <BrainCogIcon
                className={`${isCollapsed ? 'mx-auto' : 'mr-3'} h-6 w-6 text-gray-500`}
              />
              {!isCollapsed && <span>Profile Metadata</span>}
            </button>
          </div>
          {/* Submenu for profileMeta */}
          {profileMeta && (
            <div className={`mt-1 space-y-1 transition-all duration-200 ease-in-out ${isCollapsed ? 'pl-0' : 'pl-6'}`}>
              {/* <NavLink
                to="/profile-meta/integrations"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><Webhook className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <Webhook className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Social Integrations</span>
                  </span>
                )}
              </NavLink> */}
              <NavLink
                to="/profile-meta/prompt-group"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><AlignJustify className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <AlignJustify className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Prompt Group</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/profile-meta/question-group"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><FileQuestion className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <FileQuestion className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Question Group</span>
                  </span>
                )}
              </NavLink>
            </div>
          )}
        </div>
        <div className={isCollapsed ? 'px-2' : ''}>
          <div className={isCollapsed ? 'text-center' : ''}>
            <button
              onClick={() => setActionLogs(!actionLogs)}
              className={`w-full text-left group flex items-center px-2 py-2 text-sm font-medium rounded-md ${location.pathname.startsWith('/action-logs')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`
              }>
              <FilesIcon
                className={`${isCollapsed ? 'mx-auto' : 'mr-3'} h-6 w-6 text-gray-500`}
              />
              {!isCollapsed && <span>Action Logs</span>}
            </button>
          </div>
          {/* Submenu for Action Logs */}
          {actionLogs && (
            <div className={`mt-1 space-y-1 transition-all duration-200 ease-in-out ${isCollapsed ? 'pl-0' : 'pl-6'}`}>
              <NavLink
                to="/action-logs/like"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><HeartIcon className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <HeartIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Like</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/action-logs/superlike"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><HeartPulseIcon className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <HeartPulseIcon className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Super Like</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/action-logs/pass-reject"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><ListTodo className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <ListTodo className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Pass/Reject</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/action-logs/profile"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><Eye className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <Eye className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>View Profile</span>
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/action-logs/match"
                className={({ isActive }) =>
                  `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }>
                {isCollapsed ? (
                  <span className="text-xs"><Users className='h-6 w-6 p-1 bg-gray-100 text-gray-500' /></span>
                ) : (
                  <span className="flex items-center space-x-2 justify-center text-gray-700">
                    <Users className="h-6 w-6 p-1 bg-gray-100 text-gray-500 rounded" />
                    <span>Match</span>
                  </span>
                )}
              </NavLink>
            </div>
          )}
        </div>
      </nav>
      {/* User info at bottom */}
      <div className="fixed bg-white bottom-0 w-full border-t border-gray-200 p-4">
        <div className="flex items-center">
          <img
            className="h-10 w-10 rounded-full object-cover"
            src={`${REACT_APP_IMG_URL}/${userData?.profile_pic}`}
            alt="Profile"
          />
          {!isCollapsed && (
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-700">{userData?.full_name}</p>
              <p className="text-xs text-gray-500 truncate">{userData?.email}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
export default Sidebar;